import React from 'react';
import Image from 'next/image';
import IMAGE_SIZE, {
  type ImageSize
} from '../../constants/image-size.constants';
import { getImageUrl } from '../../services/media.service';
import BREAKPOINTS from '@/constants/breakpoints.constants';
import { stringHasValue } from '@/utils/data';
import type { TypeImageWithoutUnresolvableLinksResponse } from '@/contentful/types';

type PictureProps = {
  image?: TypeImageWithoutUnresolvableLinksResponse;
  src?: string;
  alt?: string;
  width?: ImageSize;
  style?: React.CSSProperties;
  quality?: number;
  priority?: boolean;
};

const Picture = ({
  image,
  src,
  alt,
  width,
  style,
  quality = 75,
  priority = false
}: PictureProps) => {
  const tabletImageWidth = IMAGE_SIZE.TABLET_IMAGE as number;

  //#region Maximum image width
  /**
   * Determine the maximum allowed width of the image
   * - If `width` was supplied...
   *   - ...as a number: use whichever is larger of that defined width and the max tablet image width
   *   - ...as an object: use whichever width value is largest
   * - ...else, default to the max tablet image width
   */
  const maxWidth =
    (width
      ? typeof width === 'number'
        ? Math.max(width, tabletImageWidth)
        : Math.max(...Object.values(width))
      : image?.fields?.image?.fields?.file?.details?.image?.width) ??
    tabletImageWidth;
  //#endregion Maximum image width

  //#region Responsive image sizes
  const sizes: { maxWidth: number; imgWidth: number }[] = [];
  if (width) {
    if (typeof width === 'number') {
      if (maxWidth > tabletImageWidth) {
        sizes.push({
          maxWidth: BREAKPOINTS.TABLET,
          imgWidth: tabletImageWidth
        });
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { MAX, ...breakpoints } = width;
      Object.entries(breakpoints).forEach(([breakpoint, imgWidth]) => {
        sizes.push({
          maxWidth: BREAKPOINTS[breakpoint as keyof typeof BREAKPOINTS],
          imgWidth
        });
      });
    }
  }
  //#endregion Responsive image sizes

  //#region Image URL
  let url;
  if (
    image?.fields?.image?.fields?.file?.url.includes('images.ctfassets.net')
  ) {
    url = getImageUrl(image.fields.image.fields.file.url);
    image.fields.image.fields.file.url = url?.href ?? '';
  }
  if (src && stringHasValue(src)) {
    url = getImageUrl(src);
  }
  url?.searchParams.set('w', `${maxWidth}`);
  url?.searchParams.set('q', `${quality ?? 75}`);
  //#endregion Image URL

  return url ? (
    <picture className='article-content-image'>
      <Image
        style={{
          maxWidth: '100%',
          ...style
        }}
        quality={quality}
        width={
          image?.fields?.image?.fields?.file?.details?.image?.width ?? maxWidth
        }
        height={
          image?.fields?.image?.fields?.file?.details?.image?.height ?? maxWidth
        }
        src={url.href}
        alt={
          alt && stringHasValue(alt)
            ? alt
            : (image?.fields?.imageDesc ?? image?.fields?.prefix ?? '')
        }
        sizes={
          sizes.length > 0
            ? [
                ...sizes
                  .sort((a, b) => a.maxWidth - b.maxWidth)
                  .map(
                    (size) =>
                      `(max-width: ${size.maxWidth}px) ${size.imgWidth}px`
                  ),
                `${width && typeof width !== 'number' && typeof width.MAX === 'number' ? width.MAX : maxWidth}px`
              ].join(', ')
            : `${maxWidth}px`
        }
        priority={priority}
      />
    </picture>
  ) : null;
};
export default Picture;
